import { AppSettings } from "@/AppSettings";
import { useMemo } from "react";
import { createItemConfiguration } from "./useItemConfiguration";
import { ConfigurationSourceDataMap, Metadata, MetadataItem, Trial } from "@/api/dtos";
import { createItemGroupConfiguration } from "./useItemGroupConfiguration";

export enum ItemType {
    Item = 1,
    ItemGroup = 2
}

export interface ItemConfiguration {
    type: ItemType.Item;
    item: NonNullable<ReturnType<typeof createItemConfiguration>>;
    order: number;
}

export interface ItemGroupConfiguration {
    type: ItemType.ItemGroup;
    itemGroup: NonNullable<ReturnType<typeof createItemGroupConfiguration>>;
    items: Array<ItemConfiguration>;
    order: number;
}

const useItemConfigurations = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    trial?: Trial, 
    configurationSourceDataId?: number,
    studyEventOid?: string,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    itemGroupName?: string,
    baseRoute?: string
) => {
    return useMemo(() => {
        if (
            !caseReportFormOid ||
            !caseReportFormVersionOid ||
            !metadata || 
            (studyEventOid && !metadata.eventForms?.[studyEventOid]?.[caseReportFormOid]) ||
            !metadata.items?.[caseReportFormOid]?.[caseReportFormVersionOid]
        ) {
            return undefined;
        }
        //NonNullable<ReturnType<typeof createItemConfiguration>>
        return Object.keys(metadata.items[caseReportFormOid][caseReportFormVersionOid]).reduce<Array<ItemConfiguration | ItemGroupConfiguration>>((items, igName) => {
            if (itemGroupName && itemGroupName !== igName) {
                return items;
            }

            if (igName === AppSettings.metadata.itemGroupUngroupedKey || !!itemGroupName) {
                const groupItems = Object.keys(metadata.items![caseReportFormOid][caseReportFormVersionOid][igName]).map((itemName) => {
                    const item = createItemConfiguration(
                        metadata, 
                        trialConfiguration?.items, 
                        configuration?.items, 
                        trial, 
                        configuration?.configuration?.isGlobal,
                        configuration?.configuration?.id ?? configurationSourceDataId, 
                        studyEventOid,
                        caseReportFormOid,
                        caseReportFormVersionOid,
                        igName === AppSettings.metadata.itemGroupUngroupedKey ? undefined : igName,
                        itemName,
                        baseRoute
                    );

                    return {
                        type: ItemType.Item,
                        item: item,
                        order: item?.itemMetadata.order ?? 0
                    } as ItemConfiguration
                })
                .filter(x => !!x.item);

                return [...items, ...groupItems];
            }
            else {
                const group = createItemGroupConfiguration(
                    metadata, 
                    trialConfiguration?.itemGroups, 
                    configuration?.itemGroups, 
                    trial, 
                    configuration?.configuration?.isGlobal,
                    configuration?.configuration?.id ?? configurationSourceDataId, 
                    studyEventOid,
                    caseReportFormOid,
                    caseReportFormVersionOid,
                    igName,
                    baseRoute
                );

                const groupItems = Object.keys(metadata.items![caseReportFormOid][caseReportFormVersionOid][igName]).map((itemName) => {
                    const item = createItemConfiguration(
                        metadata, 
                        trialConfiguration?.items, 
                        configuration?.items, 
                        trial, 
                        configuration?.configuration?.isGlobal,
                        configuration?.configuration?.id ?? configurationSourceDataId,  
                        studyEventOid,
                        caseReportFormOid,
                        caseReportFormVersionOid,
                        igName,
                        itemName,
                        baseRoute
                    );

                    return {
                        type: ItemType.Item,
                        item: item,
                        order: item?.itemMetadata.order ?? 0
                    } as ItemConfiguration
                })
                .filter(x => !!x.item)
                .sort((a, b) => a.order < b.order ? -1 : 1)

                return [ 
                    ...items, 
                    {
                        type: ItemType.ItemGroup,
                        itemGroup: group,
                        items: groupItems,
                        order: groupItems.reduce<number | undefined>((min, current) => {
                            if (!min || min > current.order) {
                                return current.order;
                            }

                            return min;
                        }, undefined) ?? 0
                    } as ItemGroupConfiguration
                ]
            }
        }, [])
        .filter(x => !!x)
        .sort((a, b) => a.order < b.order ? -1 : 1)
    }, [metadata, trialConfiguration?.items, configuration?.items, trialConfiguration?.itemGroups, configuration?.itemGroups, trial, configurationSourceDataId, studyEventOid, caseReportFormOid, caseReportFormVersionOid, itemGroupName, baseRoute]) 
};

export default useItemConfigurations;